import i18n from '@/utils/i18n';
import React, { useCallback, useState } from 'react';
import validator from 'validator';
import { createStyles } from '@/contexts/theme-context';
import { ItemDetailTabKey } from '@/enums';
import { MAX_CART_QUANTITY } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import {
  selectSalesPortalCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { SidePanel } from '@usepepper/sprout-kit-ui';
import { TextInput, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import type {
  ItemDetailItemInfo,
  PricingSummary,
} from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';

/**
 * Props for {@link ItemDetailPanelFooter}.
 */
export interface ItemDetailPanelFooterProps {
  /** Currently selected tab; accepted for future tab-specific behavior. */
  activeTab: ItemDetailTabKey;
  /** When true, the quantity input is rendered as disabled (e.g. account on hold). */
  isQuantityDisabled?: boolean;
  /** Loaded item identity; supplies the pack-size and SKU labels. */
  itemInfo: ItemDetailItemInfo;
  /** Read-only price / per-unit-price display strings. */
  pricingSummary: PricingSummary;
  /** Persists the in-memory cart to the backend on blur. Resolves once the
   *  save round-trip completes; provided by the container so the footer stays
   *  presentational. */
  saveCart: () => Promise<boolean>;
  /** Variant pack the panel was opened for; bound to the cart store. */
  variantPackUUID: string;
}

/**
 * Docked footer for the item-detail panel. Mirrors the order-entry table row's
 * quantity input so edits made in the panel persist to the same cart-store
 * entry (single source of truth).
 *
 * `saveCart` is supplied by the container (`ItemDetailContent`) rather than
 * called here, keeping the footer presentational. Note: this path doesn't go
 * through the `OrderEntryScreenContent` commission-stale wrapper, so the
 * commission badge may lag by one refetch when edits originate from the
 * panel — acceptable for now; see SRG-180 follow-up for full wiring.
 *
 * @param props - {@link ItemDetailPanelFooterProps}.
 * @returns The rendered footer row.
 */
export const ItemDetailPanelFooter: React.FC<ItemDetailPanelFooterProps> = ({
  isQuantityDisabled,
  itemInfo,
  pricingSummary,
  saveCart,
  variantPackUUID,
}) => {
  const packLabel = [itemInfo.packDisplayString, itemInfo.externalItemId]
    .filter(Boolean)
    .join(' • ');
  const priceLabel = [
    pricingSummary.priceDisplay,
    pricingSummary.unitPriceDisplay,
  ]
    .filter(Boolean)
    .join(' • ');
  const [styles, theme] = useStyles();
  const [isDirty, setIsDirty] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const savedQuantity = useCartStore(
    (state) => selectSalesPortalCartItem(state, variantPackUUID)?.quantity ?? 0,
  );

  const handleChangeText = useCallback(
    (text: string) => {
      if (isQuantityDisabled) {
        return;
      }
      if (!validator.isDecimal(text) && text !== '') {
        return;
      }
      const next = text === '' ? 0 : validator.toFloat(text);
      if (next === savedQuantity) {
        return;
      }
      if (
        useCartStore.getState().updateCartItemQuantity(variantPackUUID, next)
      ) {
        setIsDirty(true);
      }
    },
    [isQuantityDisabled, savedQuantity, variantPackUUID],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (isDirty) {
      if (savedQuantity > MAX_CART_QUANTITY) {
        useCartStore
          .getState()
          .updateCartItemQuantity(variantPackUUID, MAX_CART_QUANTITY);
      }
      setIsDirty(false);
      void saveCart();
    }
  }, [isDirty, saveCart, savedQuantity, variantPackUUID]);

  return (
    <SidePanel.Footer
      backgroundColor={theme.sk.color.bg.primary.default}
      borderTopColor={theme.sk.color.border.primary}
      borderTopWidth={1}
      gap={theme.sk.spacing.spaceSmall}
      justifyContent="space-between"
    >
      <View style={styles.labelColumn}>
        <Typography
          color={theme.sk.color.text.secondary}
          numberOfLines={1}
          variant="paragraph-md"
        >
          {packLabel}
        </Typography>
        {priceLabel ? (
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            variant="paragraph-md-600"
          >
            {priceLabel}
          </Typography>
        ) : null}
      </View>
      <TextInput
        accessibilityLabel={i18n.t(
          'sales_portal.order_entry.item_detail.a11y.quantity_input',
        )}
        accessibilityState={{ disabled: !!isQuantityDisabled }}
        aria-disabled={isQuantityDisabled}
        editable={!isQuantityDisabled}
        keyboardType="decimal-pad"
        onBlur={handleBlur}
        onChangeText={handleChangeText}
        onFocus={() => setIsFocused(true)}
        placeholder={isFocused ? ' ' : '0'}
        placeholderTextColor={theme.sk.color.text.hint}
        selectionColor={theme.sk.color.border.input.active}
        selectTextOnFocus
        style={[
          styles.input,
          isFocused ? styles.inputFocused : null,
          isQuantityDisabled ? styles.inputDisabled : null,
        ]}
        value={savedQuantity === 0 ? '' : savedQuantity.toString()}
      />
    </SidePanel.Footer>
  );
};

const useStyles = createStyles((theme) => ({
  input: {
    ...theme.sk.typography.input.md,
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    height: 32,
    textAlign: 'center',
    width: 48,
  },
  inputDisabled: {
    backgroundColor: theme.sk.color.bg.primary.disabled,
    color: theme.sk.color.text.disabled,
  },
  inputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
  labelColumn: {
    flex: 1,
    flexDirection: 'column',
  },
}));
